import React, { useMemo } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { ChevronRight } from "@mui/icons-material";
import {
    Table,
    TableBody,
    TableContainer,
    TableRow,
    SelectChangeEvent,
    Button,
    Typography,
    Box,
    Chip,
} from "@mui/material";

import { NAVIGATE_ADMIN } from "../../../../constant";
import TableHeadList from "../../../../components/common/table/TableHeadList";
import CommonTableCell from "../../../../components/common/table/CommonTableCell";
import NoDataFound from "../../../../components/common/table/NoDataFoundModule";
import CustomPagination from "../../../../components/common/table/TablePagination";
import TableSkeleton from "../../../../components/loader/TableSkeleton";
import { Referral } from "../../../../utils/dto/module/campaign.activity";
import { openDrawer } from "../../../../redux/reducer/drawerSlice";
import { useTranslation } from "../../../../hooks/useTranslation";

interface HospitalReferredPageProps {
    beneficiaries: Referral[];
    loading: boolean;
    totalCount: number;
    setPayload: (payload: any) => void;
    payload: {
        offset: number;
        limit: number;
    };
}

const HospitalReferredPage: React.FC<HospitalReferredPageProps> = ({
    beneficiaries,
    loading,
    totalCount,
    setPayload,
    payload
}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { t, language } = useTranslation();

    // ✅ Dynamic table headers with translation
    const headCells = useMemo(() => [
        { id: "sr_no", label: t('referHospital', 'srNo'), numeric: false, width: "100px", isFilterable: false, isSorting: false },
        { id: "opd_no", label: t('referHospital', 'tokenNo'), numeric: false, width: "150px", isFilterable: false, isSorting: false },
        { id: "campaign_name", label: t('referHospital', 'campaignName'), numeric: false, width: "250px", isFilterable: false, isSorting: false },
        { id: "beneficiary_name", label: t('referHospital', 'beneficiaryName'), numeric: false, width: "200px", isFilterable: false, isSorting: false },
        { id: "hospital_name", label: t('referHospital', 'hospitalName'), numeric: false, width: "280px", isFilterable: false, isSorting: false },
        { id: "remark", label: t('referHospital', 'remark'), numeric: false, width: "200px", isFilterable: false, isSorting: false },
        { id: "date", label: t('referHospital', 'date'), numeric: false, width: "150px", isFilterable: false, isSorting: false },
        { id: "action", label: t('referHospital', 'action'), numeric: false, width: "150px", isFilterable: false, isSorting: false },
    ], [t]);

    // Unique hospitals referred to (from beneficiary referral documents).
    const getReferredHospitals = (user: Referral) => {
        const docs = user.beneficiary_doc ?? [];
        const byId = new Map<number, { id: number; name: string }>();
        docs.forEach((doc) => {
            if (doc?.hospital_id == null) return;
            const name = language === 'mr'
                ? (doc.hospital_name_mr || doc.hospital_name)
                : doc.hospital_name;
            if (name) byId.set(doc.hospital_id, { id: doc.hospital_id, name });
        });
        return Array.from(byId.values());
    };

    const handlePageChange = (_event: React.ChangeEvent<unknown>, newPage: number) => {
        setPayload((prev: { limit: number; }) => ({ ...prev, offset: (newPage - 1) * prev.limit }));
    };

    const handleRowsPerPageChange = (event: SelectChangeEvent) => {
        setPayload((prev: any) => ({ ...prev, limit: Number(event.target.value), offset: 0, }));
    };

    const handleViewReferalLetter = (user: Referral) => {
        dispatch(openDrawer({
            _Key: 'ViewReferalDetails',
            title: t('referHospital', 'viewReferralLetterDetails'),
            drawerWidth: 'lg',
            isFullScreen: false,
            drawerData: user,
        }));
    };

    return (
        <>
            <TableContainer sx={{ minHeight: 'calc(72vh - 120px)', maxHeight: 'calc(72vh - 120px)' }}>
                <Table stickyHeader size="small" sx={{ minWidth: 750, tableLayout: 'fixed' }}>
                    <TableHeadList headCells={headCells} />
                    <TableBody>
                        {!loading && beneficiaries.map((user, index) => (
                            <TableRow key={user.id} hover>
                                <CommonTableCell
                                    style={{ textAlign: 'center' }}
                                    value={payload.offset + index + 1}
                                />
                                <CommonTableCell
                                    style={{ textAlign: 'center' }}
                                    value={
                                        <Typography
                                            onClick={() => handleViewReferalLetter(user)}
                                            sx={{ color: 'blue', fontWeight: 500, cursor: 'pointer' }}
                                        >
                                            {user.token_no ?? t('referHospital', 'notAvailable')}
                                        </Typography>
                                    }
                                />
                                <CommonTableCell
                                    value={user.campaign_name ?? t('referHospital', 'noValue')}
                                />
                                <CommonTableCell
                                    value={user.beneficiary_name ?? t('referHospital', 'noValue')}
                                />
                                <CommonTableCell
                                    value={(() => {
                                        const hospitals = getReferredHospitals(user);
                                        if (hospitals.length === 0) return t('referHospital', 'noValue');
                                        return (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {hospitals.map((h) => (
                                                    <Chip
                                                        key={h.id}
                                                        label={h.name}
                                                        size="small"
                                                        color="primary"
                                                        variant="outlined"
                                                    />
                                                ))}
                                            </Box>
                                        );
                                    })()}
                                />
                                <CommonTableCell
                                    value={user.remark ?? t('referHospital', 'noValue')}
                                />
                                <CommonTableCell
                                    value={user.date ?? t('referHospital', 'noValue')}
                                    formatDate
                                    style={{ textAlign: 'center' }}
                                />
                                <CommonTableCell
                                    value={
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            onClick={() => navigate(`${NAVIGATE_ADMIN.BENEFICIARY_DETAIL_PAGE}?uuid=${user.beneficiary_uuid}`)}
                                            endIcon={<ChevronRight />}
                                        >
                                            {t('referHospital', 'viewProfile')}
                                        </Button>
                                    }
                                    style={{ textAlign: 'center' }}
                                />
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {!loading && beneficiaries?.length === 0 && (
                    <NoDataFound
                        height="50vh"
                        moduleName={t('referHospital', 'referredListModule')}
                    />
                )}
                {loading && <TableSkeleton columns={headCells.length} rows={15} />}
            </TableContainer>
            <CustomPagination
                count={totalCount}
                rowsPerPage={payload.limit}
                page={payload.offset / payload.limit + 1}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[15, 20, 25, 30]}
            />
        </>
    );
};

export default HospitalReferredPage;
